import {
  openDB,
  type DBSchema,
  type IDBPDatabase,
  type IDBPTransaction,
  type StoreNames as IDBStoreNames,
  type StoreValue as IDBStoreValue,
  type StoreKey as IDBStoreKey
} from 'idb'
import type { Employee } from '@/lib/services/employees'
import type { Qualification } from '#modules/qualifications'
import type {
  UserIncidentUnit,
  UserUnit,
  UserIncident
} from '@/contexts/UserContext'
import type {
  Database,
  Tables,
  TablesInsert,
  TablesUpdate
} from '#modules/database'
import { timestampNow, type Timestamp } from '#modules/timestamp'

// -- IndexedDB schema ---------------------------------------------------------

// Row id type
// Non-null so the id is always a usable IndexedDB key.
type RowId<Name extends TableName> = Tables<Name> extends { id: infer IdT } ? NonNullable<IdT> : never

// Offline write queue
// Data is typed at the queue boundary, stored untyped as it varies by table.
export type TableName = keyof Database['public']['Tables'] & string
export type WriteOp = 'insert' | 'update' | 'delete'
export interface PendingWrite {
  id: number
  table: TableName
  operation: WriteOp
  data: Record<string, unknown>
  timestamp: Timestamp
}

// Cached assignment context for the current user
export type CachedAssignment = {
  employee: Employee
  unit: UserUnit | null
  incidentUnit: UserIncidentUnit | null
  incident: UserIncident | null
}

interface FirePCRDB extends DBSchema {
  // public cache
  qualifications: { key: RowId<'qualifications'>; value: Qualification }
  encounters: { key: string; value: any; indexes: { 'by-incident': string } }
  mar_entries: { key: string; value: any; indexes: { 'by-incident': string } }
  incidents: { key: string; value: any }
  units: { key: string; value: any }
  employees: { key: string; value: any }
  formulary: { key: string; value: any }
  item_catalog: { key: string; value: any }
  inventory: { key: string; value: any }
  supply_runs: { key: string; value: any }
  supply_run_items: { key: string; value: any; indexes: { 'by-supply-run': string } }
  incident_units: { key: string; value: any }
  ics214s: { key: string; value: any }
  ics214_activities: { key: string; value: any }
  ics214_personnel: { key: string; value: any }
  vitals: { key: string; value: any; indexes: { 'by-encounter': string } }
  progress_notes: { key: string; value: any; indexes: { 'by-encounter': string } }
  procedures: { key: string; value: any; indexes: { 'by-encounter': string } }
  // internal
  pending_sync: { key: number; value: PendingWrite }
  sync_meta: { key: string; value: { key: string; lastSynced: Timestamp } }
  current_employee: { key: string; value: { key: string } & CachedAssignment }
}

// Version-change transaction spanning every store, handed to each upgrade step so
// it can reshape existing records in place (cursor + update) without dropping them.
type UpgradeTx = IDBPTransaction<FirePCRDB, IDBStoreNames<FirePCRDB>[], 'versionchange'>

// Version update callback.
type UpgradeCb = (db: IDBPDatabase<FirePCRDB>, tx: UpgradeTx) => void | Promise<void>

// Keyed by target DB version. Append the next integer to add a schema change;
// never renumber or reorder existing entries. Every entry with a version above
// the store's current version runs, in ascending order, in one upgrade transaction.
//
// Migration rule - offline durability:
//   This upgrade can run while offline. A PWA caches the new build via the
//   service worker while online, then activates it later (possibly deep
//   offline), and the first openDB() call runs these steps with no network. So a
//   store cleared here cannot re-sync until connectivity returns.
//   Reshape a record's fields in place via the version-change transaction
//   (iterate the store, cursor.update the row). Do NOT delete and recreate.
//   deleteObjectStore / store.clear() destroy every row:
//     - offline-authored stores (pending_sync, or anything fed by an offline
//       write path) -> PERMANENT data loss. Never clear.
//     - server-derived caches (incidents, units, formulary, sync_meta, ...) ->
//       data unavailable until back online. Clear only if the old shape is
//       genuinely unusable, accepting that offline gap.
//   Default: reshape in place.
//   Async steps run sequentially: the upgrade loop awaits each before the
//   next, so a later step never overlaps an earlier one. This holds only if a
//   step awaits all its own IDB work before returning: a step whose promise
//   resolves with writes still outstanding lets the next step (and the tx
//   commit) race it. And only ever await IDB operations on the passed-in tx;
//   awaiting any non-IDB promise (fetch, timer) lets the version-change
//   transaction auto-commit mid-step, and remaining work throws
//   TransactionInactiveError.
//   Cross-deploy compatibility of already-queued pending_sync writes is a
//   separate concern, see docs/open-items.md.
const dbUpgrades: Record<number, UpgradeCb> = {
  1: db => {
    const encStore = db.createObjectStore('encounters', { keyPath: 'id' })
    encStore.createIndex('by-incident', 'incident_id')

    const marStore = db.createObjectStore('mar_entries', { keyPath: 'id' })
    marStore.createIndex('by-incident', 'incident_id')

    db.createObjectStore('incidents', { keyPath: 'id' })
    db.createObjectStore('units', { keyPath: 'id' })
    db.createObjectStore('employees', { keyPath: 'id' })
    db.createObjectStore('formulary', { keyPath: 'id' })
    db.createObjectStore('pending_sync', { keyPath: 'id', autoIncrement: true })
    db.createObjectStore('sync_meta', { keyPath: 'key' })
  },
  2: db => {
    db.createObjectStore('inventory', { keyPath: 'id' })
    db.createObjectStore('supply_runs', { keyPath: 'id' })

    const vitalsStore = db.createObjectStore('vitals', { keyPath: 'id' })
    vitalsStore.createIndex('by-encounter', 'encounter_id')
  },
  3: db => {
    const pnStore = db.createObjectStore('progress_notes', { keyPath: 'id' })
    pnStore.createIndex('by-encounter', 'encounter_id')

    const procStore = db.createObjectStore('procedures', { keyPath: 'id' })
    procStore.createIndex('by-encounter', 'encounter_id')
  },
  4: db => {
    db.createObjectStore('incident_units', { keyPath: 'id' })
  },
  5: db => {
    db.createObjectStore('ics214s', { keyPath: 'id' })
  },
  6: db => {
    db.createObjectStore('ics214_activities', { keyPath: 'id' })
    db.createObjectStore('ics214_personnel', { keyPath: 'id' })
  },
  7: db => {
    const sriStore = db.createObjectStore('supply_run_items', { keyPath: 'id' })
    sriStore.createIndex('by-supply-run', 'supply_run_id')
  },
  8: db => {
    db.createObjectStore('item_catalog', { keyPath: 'id' })
  },
  9: db => {
    db.createObjectStore('qualifications', { keyPath: 'id' })
  },
  10: db => {
    // progress_notes.author_role renamed to author_qual; clear store to force re-sync.
    db.deleteObjectStore('progress_notes')
    const pnStore = db.createObjectStore('progress_notes', { keyPath: 'id' })
    pnStore.createIndex('by-encounter', 'encounter_id')
  },
  11: db => {
    db.createObjectStore('current_employee', { keyPath: 'key' })
  },
  12: db => {
    // clear current_employee to force re-cache with assignment data
    db.deleteObjectStore('current_employee')
    db.createObjectStore('current_employee', { keyPath: 'key' })
  },
  13: async (_db, tx) => {
    // Drop unused pendingCount field from sync_meta.
    const store = tx.objectStore('sync_meta')
    for await (const cursor of store.iterate()) {
      const prev = cursor.value as {
        key: string
        lastSynced: Timestamp
        pendingCount: number
      }
      await cursor.update({
        key: prev.key,
        lastSynced: prev.lastSynced
      })
    }
  },
  14: async (_db, tx) => {
    // Older builds marked a flushed write synced:true instead of deleting it and
    // stamped an error field on failures. getPendingWrites no longer filters on
    // synced, so a synced:true row would re-flush. Delete synced:true rows;
    // reshape survivors to drop synced/error.
    // The row shape is written out in full and frozen to this migration: it is
    // the on-disk shape as of v14, deliberately not tied to PendingWrite so a
    // later change to that type can't silently alter what this step reads.
    type LegacyRow = {
      id: number
      table: string
      operation: 'insert' | 'update' | 'delete'
      data: Record<string, unknown>
      timestamp: string
      synced?: boolean
      error?: string
    }
    const store = tx.objectStore('pending_sync')
    for await (const cursor of store.iterate()) {
      const row = cursor.value as LegacyRow
      if (row.synced) {
        await cursor.delete()
        continue
      }
      await cursor.update({
        id: row.id,
        table: row.table as TableName,
        operation: row.operation,
        data: row.data,
        timestamp: row.timestamp as Timestamp
      })
    }
  }
}

// Integer keys enumerate in order.
const dbVersions = Object.keys(dbUpgrades).map(Number)

const DB_NAME = `firepcr-offline-${import.meta.env.VITE_SUPABASE_PROJECT_REF as string ?? 'local'}`
const DB_VERSION = dbVersions[dbVersions.length - 1]

// One-time cleanup of client-side artifacts left by superseded builds.
function purgeLegacyClientStorage() {
  if (typeof window === 'undefined') return
  // Pre-project-keyed DB name (before the firepcr-offline-<ref> scheme).
  indexedDB.deleteDatabase('firepcr-offline')
  // Cold-sync timestamps from the removed cold/hot sync mechanism.
  for (const key of Object.keys(localStorage))
    if (key.startsWith('firepcr-last-cold-sync-')) localStorage.removeItem(key)
}

// Wipes all client-side cache: every firepcr-* IndexedDB store plus the persisted
// auth token. The recovery path for a bad persisted state. The firepcr- prefix
// matches all project-keyed DBs regardless of ref.
export async function clearAllCaches(): Promise<void> {
  if (typeof window === 'undefined') return
  dbPromise = null
  const dbs = (await indexedDB.databases?.().catch(() => [])) ?? []
  await Promise.all(
    dbs
      .map((d) => d.name)
      .filter((n): n is string => !!n && n.startsWith('firepcr-'))
      .map((n) => new Promise<void>((resolve) => {
        const req = indexedDB.deleteDatabase(n)
        req.onsuccess = req.onerror = req.onblocked = () => resolve()
      })),
  )
  // Drop the persisted session (sb-<ref>-auth-token) and any firepcr-* keys so
  // the next login starts clean.
  for (const key of Object.keys(localStorage))
    if (key.startsWith('sb-') || key.startsWith('firepcr-')) localStorage.removeItem(key)
}

let dbPromise: Promise<IDBPDatabase<FirePCRDB>> | null = null

function getDB() {
  if (typeof window === 'undefined') return null as any
  if (!dbPromise) {
    dbPromise = openDB<FirePCRDB>(DB_NAME, DB_VERSION, {
      async upgrade(db, oldVersion, _newVersion, tx) {
        purgeLegacyClientStorage()
        for (const version of dbVersions)
          if (oldVersion < version)
            await dbUpgrades[version](db, tx)
      },
    })
  }
  return dbPromise
}

// -- Cache functions ----------------------------------------------------------

// Store-name, key, and value types derived from the FirePCRDB schema. Generic
// cache callers constrain on CacheNames so the store name stays a literal, which
// lets CacheKey/CacheType resolve the primary-key and record types for that exact store.
// pending_sync, sync_meta, and current_employee are excluded: they are internal
// plumbing stores managed by their own dedicated functions, not the generic cache layer.
export type CacheNames = Exclude<IDBStoreNames<FirePCRDB>, 'pending_sync' | 'sync_meta' | 'current_employee'>
export type CacheKey<Name extends CacheNames> = IDBStoreKey<FirePCRDB, Name>
export type CacheType<Name extends CacheNames> = IDBStoreValue<FirePCRDB, Name>

// replace=true clears the store before writing (correct for full-snapshot reference data).
// replace=false upserts without clearing (correct for append/windowed data like hot sync).
export async function cacheData<Name extends CacheNames>(
  cacheName: Name,
  data: CacheType<Name>[],
  replace = false,
): Promise<void> {
  const db = await getDB()
  if (!db) return
  const tx = db.transaction(cacheName, 'readwrite')
  if (replace) await tx.store.clear()
  for (const item of data) {
    await tx.store.put(item)
  }
  await tx.done
}

export async function getCachedData<Name extends CacheNames>(
  cacheName: Name,
): Promise<CacheType<Name>[]> {
  const db = await getDB()
  if (!db) return []
  return db.getAll(cacheName)
}

export async function getCachedById<Name extends CacheNames>(
  cacheName: Name,
  id: CacheKey<Name>,
): Promise<CacheType<Name> | undefined> {
  const db = await getDB()
  if (!db) return undefined
  return db.get(cacheName, id)
}

export async function cacheCurrentEmployee(
  employee: Employee,
  unit: UserUnit | null,
  incidentUnit: UserIncidentUnit | null,
  incident: UserIncident | null
): Promise<void> {
  const db = await getDB()
  if (!db) return
  await db.put('current_employee', {
    key: 'me',
    employee,
    unit,
    incidentUnit,
    incident
  })
}

export async function getCachedCurrentEmployee(): Promise<CachedAssignment | null> {
  const db = await getDB()
  if (!db) return null
  const entry = await db.get('current_employee', 'me')
  if (!entry) return null
  return {
    employee: entry.employee,
    unit: entry.unit,
    incidentUnit: entry.incidentUnit,
    incident: entry.incident
  }
}

// -- Pending sync queue -------------------------------------------------------

// Discriminates the enqueue payload by table + operation against the generated
// Supabase types: the type gate callers pass through to queue a write.
export type WriteData<Name extends TableName, Op extends WriteOp> =
  Op extends 'insert' ? TablesInsert<Name> :
  Op extends 'update' ? TablesUpdate<Name> & { id: RowId<Name> } :
  { id: RowId<Name> }

export async function queueOfflineWrite<T extends TableName, Op extends WriteOp>(
  table: T,
  operation: Op,
  data: WriteData<T, Op>
): Promise<void> {
  const db = await getDB()
  if (!db) return
  await db.add('pending_sync', {
    table,
    operation,
    data: data as Record<string, unknown>,
    timestamp: timestampNow()
  })
}

export async function getPendingWrites(): Promise<PendingWrite[]> {
  const db = await getDB()
  if (!db) return []
  return db.getAll('pending_sync')
}

export async function markSynced(id: number): Promise<void> {
  const db = await getDB()
  if (!db) return
  await db.delete('pending_sync', id)
}

export async function getPendingCount(): Promise<number> {
  const pending = await getPendingWrites()
  return pending.length
}

// -- Sync metadata ------------------------------------------------------------

// Single global sync-meta record; the store holds one row under this key.
// Records when cached data was last refreshed from the server.
const SYNC_META_KEY = 'global'

export async function updateSyncMeta(lastSynced: Timestamp): Promise<void> {
  const db = await getDB()
  if (!db) return
  await db.put('sync_meta', { key: SYNC_META_KEY, lastSynced })
}

export async function getSyncMeta(): Promise<{ lastSynced: Timestamp } | null> {
  const db = await getDB()
  if (!db) return null
  return (await db.get('sync_meta', SYNC_META_KEY)) ?? null
}
